import type { Money } from '@platform/design-system';
import { nameIn } from '../catalog/categories.ts';
import { variantLabel, type OptionIndex } from '../catalog/options.ts';
import type { Product } from '../catalog/product.ts';

export const LABEL_FORMATS = ['sheet', 'roll'] as const;
export type LabelFormat = (typeof LABEL_FORMATS)[number];

export const SYMBOLOGIES = ['code128', 'qr'] as const;
export type Symbology = (typeof SYMBOLOGIES)[number];

export const MAX_COPIES_PER_VARIANT = 500;
export const MAX_LABEL_PRODUCTS = 50;
export const MAX_STICKERS = 1000;
export const STICKERS_PER_SHEET = 24;

export type LabelSubject = {
  readonly variantId: string;
  readonly locale: string;
  readonly name: string;
  readonly options: string;
  readonly sku: string;
  readonly price: Money;
};

export type Copies = Readonly<Record<string, number>>;

export type LabelFocus = { readonly variantId: string; readonly quantity: number };

export type LabelSelection = { readonly productIds: readonly string[]; readonly focus: LabelFocus | null };

export const labelSubjectsOf = (
  products: readonly Product[],
  indexFor: (locale: string) => OptionIndex,
  currency: string,
): readonly LabelSubject[] =>
  products.flatMap((product) => {
    const locale = product.defaultLocale;
    const name = nameIn(product, locale, product.reference ?? product.id);
    return product.variants.map((variant) => ({
      variantId: variant.id,
      locale,
      name,
      options: variantLabel(variant.optionValueIds, indexFor(locale), ''),
      sku: variant.sku,
      price: { amount: variant.price.amount, currency },
    }));
  });

const initialCopiesOf = (subject: LabelSubject, focus: LabelFocus | null): number => {
  if (focus === null) return 1;
  return subject.variantId === focus.variantId ? focus.quantity : 0;
};

export const copiesOf = (
  subjects: readonly LabelSubject[],
  focus: LabelFocus | null,
  overrides: Copies,
): Copies =>
  Object.fromEntries(
    subjects.map((subject) => [
      subject.variantId,
      overrides[subject.variantId] ?? initialCopiesOf(subject, focus),
    ]),
  );

export const clampCopies = (value: number): number =>
  Number.isFinite(value) ? Math.min(MAX_COPIES_PER_VARIANT, Math.max(0, Math.trunc(value))) : 0;

export const totalOf = (copies: Copies): number =>
  Object.values(copies).reduce((total, count) => total + count, 0);

export const stickersOf = (subjects: readonly LabelSubject[], copies: Copies): readonly LabelSubject[] =>
  subjects.flatMap((subject) => Array.from({ length: copies[subject.variantId] ?? 0 }, () => subject));

export type Slot = LabelSubject | null;

const chunked = (slots: readonly Slot[], size: number): readonly (readonly Slot[])[] =>
  Array.from({ length: Math.ceil(slots.length / size) }, (_, page) =>
    slots.slice(page * size, (page + 1) * size),
  );

export const pagesOf = (
  stickers: readonly LabelSubject[],
  format: LabelFormat,
  startAt: number,
): readonly (readonly Slot[])[] => {
  if (stickers.length === 0) return [];
  if (format === 'roll') return chunked(stickers, 1);
  const skipped: Slot[] = Array.from({ length: startAt - 1 }, () => null);
  return chunked([...skipped, ...stickers], STICKERS_PER_SHEET);
};
